import { ChevronDownIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { ReportSection, ReportStat, ReportTable } from '@/lib/reports';
import { cn } from '@/lib/utils';

/** Surface every block on the Overview shares: title row, optional meta and action, content. */
export function Card({ title, meta, action, children, className, padded = true }: { title: ReactNode; meta?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; padded?: boolean }) {
  return (
    <section className={cn('bg-card flex min-w-0 flex-col border', className)} aria-label={typeof title === 'string' ? title : undefined}>
      <div className={cn('flex items-start justify-between gap-3 px-5 pt-5', padded ? 'mb-4' : 'mb-3')}>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
          {meta && <p className="text-muted-foreground mt-0.5 text-xs">{meta}</p>}
        </div>
        {action}
      </div>
      <div className={cn('min-w-0 flex-1', padded && 'px-5 pb-5')}>{children}</div>
    </section>
  );
}

/** A row of small figures: value first, label under it. */
export function StatStrip({ stats, className }: { stats: ReportStat[]; className?: string }) {
  if (stats.length === 0) return null;
  return (
    <dl className={cn('flex flex-wrap gap-x-8 gap-y-3', className)}>
      {stats.map((s) => (
        <div key={s.label} className="min-w-0">
          <dd className="text-xl leading-none font-semibold tracking-tight tabular-nums">{s.value}</dd>
          <dt className="text-muted-foreground mt-1 text-xs">
            {s.label}
            {s.hint && <span className="text-muted-foreground/80"> · {s.hint}</span>}
          </dt>
        </div>
      ))}
    </dl>
  );
}

/** A report table that shows its first rows and grows on request, so long lists never dominate the page. */
export function CollapsibleTable({ table, initial = 6, dense }: { table: ReportTable; initial?: number; dense?: boolean }) {
  const [open, setOpen] = useState(false);
  const numeric = new Set(table.numeric ?? []);
  const rows = open ? table.rows : table.rows.slice(0, initial);
  const hidden = table.rows.length - rows.length;
  const total = table.totalRows && table.totalRows > table.rows.length ? table.totalRows : null;
  return (
    <div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {table.columns.map((c, i) => (
                <TableHead key={c} className={cn('text-[11px] tracking-wide uppercase', numeric.has(i) && 'text-right', dense && 'h-8')}>
                  {c}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, ri) => (
              <TableRow key={ri}>
                {r.map((v, ci) => {
                  const text = v === null || v === undefined ? '—' : String(v);
                  const long = !numeric.has(ci) && text.length > 24;
                  return (
                    <TableCell key={ci} className={cn('text-sm', numeric.has(ci) ? 'text-right tabular-nums' : long ? cn('truncate', dense ? 'max-w-56' : 'max-w-80') : 'whitespace-nowrap', dense && 'py-1.5')} title={long ? text : undefined}>
                      {text}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {(hidden > 0 || open && table.rows.length > initial || total) && (
        <div className="flex items-center justify-between gap-3 border-t px-1 pt-2">
          {hidden > 0 || (open && table.rows.length > initial) ? (
            <Button type="button" variant="ghost" size="xs" onClick={() => setOpen((o) => !o)}>
              <ChevronDownIcon className={cn('transition-transform', open && 'rotate-180')} aria-hidden /> {open ? 'Show fewer' : `Show all ${table.rows.length}`}
            </Button>
          ) : (
            <span />
          )}
          {total && <span className="text-muted-foreground text-xs">Showing {table.rows.length} of {total}.</span>}
        </div>
      )}
    </div>
  );
}

/** One report section as a card: its figures as a strip, its table collapsed to a few rows, or its empty line. */
export function SectionCard({ section, className, initialRows = 6, extra }: { section: ReportSection; className?: string; initialRows?: number; extra?: ReactNode }) {
  const hasTable = !!section.table && section.table.rows.length > 0;
  const stats = section.stats ?? [];
  return (
    <Card title={section.title} meta={section.description} className={className} padded={false}>
      <div className="px-5">
        <StatStrip stats={stats} className="mb-4" />
      </div>
      {extra}
      {hasTable ? (
        <div className="border-t px-1 pb-2">
          <CollapsibleTable table={section.table!} initial={initialRows} dense />
        </div>
      ) : (
        <p className="text-muted-foreground px-5 pb-5 text-sm">{section.empty ?? (stats.length ? '' : 'Nothing in this period.')}</p>
      )}
    </Card>
  );
}
